import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { accountAPI } from '../services/api';
import './LeavePlatform.css';

const LeavePlatform = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [formData, setFormData] = useState({
    password: '',
    confirmation: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name] || errors.general) {
      setErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors[name]) {
          delete newErrors[name];
        }
        if (newErrors.general) {
          delete newErrors.general;
        }
        return newErrors;
      });
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password.trim()) {
      newErrors.password = ['Password is required'];
    }

    if (!formData.confirmation.trim()) {
      newErrors.confirmation = ['Please type DELETE to confirm'];
    } else if (formData.confirmation !== 'DELETE') {
      newErrors.confirmation = ['Please type DELETE exactly to confirm'];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!showWarning) {
      setShowWarning(true);
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const response = await accountAPI.deleteAccount(formData.password, formData.confirmation);

      if (response.success) {
            // Logout user and redirect to home
            logout();
            navigate('/', { replace: true });
            // Show success message briefly before redirect
            alert(response.message || 'Your account has been deactivated. You can reactivate it within 90 days by logging in again.');
      } else {
        if (response.errors) {
          const userFriendlyErrors = {};
          
          const fieldMapping = {
            'password': 'password',
            'confirmation': 'confirmation'
          };
          
          Object.keys(response.errors).forEach(key => {
            const errorMessages = response.errors[key];
            const frontendFieldName = fieldMapping[key] || key;
            
            if (Array.isArray(errorMessages)) {
              userFriendlyErrors[frontendFieldName] = errorMessages.map(msg => {
                if (msg.includes('Password is incorrect')) {
                  return 'The password you entered is incorrect. Please check and try again.';
                } else if (msg.includes('type DELETE')) {
                  return 'Please type DELETE exactly to confirm account deletion.';
                }
                return msg;
              });
            } else {
              userFriendlyErrors[frontendFieldName] = errorMessages;
            }
          });
          
          setErrors(userFriendlyErrors);
        } else {
          setErrors({ general: response.message || 'Failed to delete account. Please try again.' });
        }
      }
    } catch (error) {
      console.error('Account deletion error:', error);
      setErrors({ general: 'Network error. Please check your connection and try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="leave-platform-container">
      <div className="leave-platform-header">
        <h2>Leave Platform</h2>
        <p>Deactivate your account. You can reactivate it within 90 days by logging in again.</p>
      </div>

      <div className="leave-platform-content">
        <div className="leave-platform-layout">
          <div className="leave-platform-form-section">
            {showWarning && (
              <div className="warning-message">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '8px' }}>Account Deactivation Notice</div>
                  <div style={{ fontSize: '14px', opacity: '0.9' }}>
                    Your account will be deactivated and hidden from the platform. However, you can come back anytime within the next 90 days and we'll welcome you back! Simply log in with your email and password to reactivate your account.
                    <br /><br />
                    <strong>After 90 days:</strong> Your account will be permanently deleted from our system and cannot be recovered.
                    <br /><br />
                    <strong>What happens when you deactivate:</strong>
                    <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                      <li>Your profile will be hidden from other users</li>
                      <li>You will be logged out from all devices</li>
                      <li>Your data will be preserved for 90 days</li>
                      <li>You can reactivate by logging in anytime within 90 days</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {errors.general && (
              <div className="error-message">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>{errors.general}</div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="leave-platform-form">
              <div className="form-group">
                <label htmlFor="password">Enter Your Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={errors.password && errors.password.length > 0 ? 'error' : ''}
                    placeholder="Enter your password to confirm"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={togglePasswordVisibility}
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && errors.password.length > 0 && (
                  <span className="error-text">{errors.password[0]}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="confirmation">Type DELETE to Confirm</label>
                <input
                  type="text"
                  id="confirmation"
                  name="confirmation"
                  value={formData.confirmation}
                  onChange={handleChange}
                  className={errors.confirmation && errors.confirmation.length > 0 ? 'error' : ''}
                  placeholder="Type DELETE"
                  disabled={isLoading}
                  style={{ textTransform: 'uppercase' }}
                />
                {errors.confirmation && errors.confirmation.length > 0 && (
                  <span className="error-text">{errors.confirmation[0]}</span>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-danger"
                  disabled={isLoading}
                  style={{ 
                    opacity: isLoading ? 0.7 : 1,
                    cursor: isLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner"></span>
                      Deleting Account...
                    </>
                  ) : (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3,6 5,6 21,6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                      {showWarning ? 'Deactivate My Account' : 'Continue to Deactivate Account'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          <div className="leave-platform-help-section">
            <div className="help-card">
              <div className="help-header">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
                <h3>Account Reactivation</h3>
              </div>
              <div className="help-content">
                <div className="help-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22,4 12,14.01 9,11.01"></polyline>
                  </svg>
                  <span>You can reactivate your account anytime within 90 days by simply logging in</span>
                </div>
                <div className="help-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2z"></path>
                    <path d="M12 6v6l4 2"></path>
                  </svg>
                  <span>After 90 days, your account will be permanently deleted</span>
                </div>
                <div className="help-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  <span>Contact support if you need assistance</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeavePlatform;
